/* PDP reviews — fills the Reviews accordion panel from Judge.me's product
   review widget.

   Judge.me keeps ownership of everything that matters: the reviews, the tabs,
   the sort, and both modals. This only reads their widget, paints the design
   with what it finds, and hides their presentation once it has something to
   show. The tabs and buttons here proxy to their real controls, so whatever
   they support is what we offer.

   Review text is copied, never moved. Re-parenting Judge.me's own card nodes
   is what makes their code re-measure a card and permanently rewrite the
   review text, and their product widget is a Vue app besides — moving its
   nodes would fight the framework's own re-renders as well. */
(function () {
  var root = document.querySelector('[data-pr-root]');
  if (!root) return;

  var LABEL = {
    verified: root.getAttribute('data-pr-verified') || 'Verified',
    buyer: root.getAttribute('data-pr-buyer') || 'Verified buyer',
    write: root.getAttribute('data-pr-write') || 'Write a review',
    ask: root.getAttribute('data-pr-ask') || 'Ask a question'
  };
  var MIN_FOR_ROWS = parseInt(root.getAttribute('data-pr-min'), 10) || 4;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var el = {
    summary: root.querySelector('[data-pr-summary]'),
    score: root.querySelector('[data-pr-score]'),
    stars: root.querySelector('[data-pr-stars]'),
    count: root.querySelector('[data-pr-count]'),
    recommend: root.querySelector('[data-pr-recommend]'),
    recPct: root.querySelector('[data-pr-rec-pct]'),
    bars: root.querySelector('[data-pr-bars]'),
    toolbar: root.querySelector('[data-pr-toolbar]'),
    tabs: root.querySelector('[data-pr-tabs]'),
    actions: root.querySelector('[data-pr-actions]'),
    rows: root.querySelector('[data-pr-rows]'),
    tracks: root.querySelectorAll('[data-pr-track]'),
    empty: root.querySelector('[data-pr-empty]')
  };

  function widget() { return document.querySelector('.jdgm-review-widget'); }

  /* ── Reading Judge.me ─────────────────────────────────────── */

  function firstText(scope, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var el = scope.querySelector(selectors[i]);
      if (el && el.textContent.trim()) return el.textContent.trim();
    }
    return '';
  }

  /* The revamp puts the number only in the star row's label; the classic
     widget carries it as data-score on its own rating element. */
  function ratingOf(scope) {
    var sr = scope.querySelector('.jm-star-rating[aria-label]');
    var m = sr && sr.getAttribute('aria-label').match(/([\d.]+)\s*out of/);
    if (m) return parseFloat(m[1]);
    var classic = scope.querySelector('[data-score]');
    return classic ? parseFloat(classic.getAttribute('data-score')) || 0 : 0;
  }

  function harvest() {
    var w = widget();
    if (!w) return null;

    /* Judge.me ships two generations of this widget and the shop can be moved
       between them from their dashboard, so both are read rather than
       whichever happens to be installed today:

         revamp (Vue)  .jdgm-review-card / __name / __body
         classic       .jdgm-rev         / .jdgm-rev__author / .jdgm-rev__body

       Without this the section goes blank the moment that setting changes. */
    var cards = Array.prototype.slice.call(w.querySelectorAll('.jdgm-review-card, .jdgm-rev'));
    var reviews = cards.map(function (card) {
      var name = firstText(card, ['.jdgm-review-card__name', '.jdgm-rev__author']);
      return {
        name: name,
        /* "Anonymous" is Judge.me's placeholder, not a person, so it gets the
           neutral mark rather than an "A" that reads as an initial. */
        initial: name && name.toLowerCase() !== 'anonymous' ? name.charAt(0).toUpperCase() : '·',
        body: firstText(card, ['.jdgm-review-card__body', '.jdgm-rev__body']),
        rating: ratingOf(card),
        verified: !!card.querySelector('.jm-verified-buyer-badge, .jdgm-rev__buyer-badge')
      };
    /* A star-only review (a rating left with no text) is still a review, and
       Judge.me counts it in the tab — dropping it made the tab say 3 over
       two cards. Only cards with neither text nor a rating are skipped: those
       are the widget's shell mid-render, not reviews. */
    }).filter(function (r) { return r.body || r.rating; });

    /* The headline score is printed as plain text beside the widget's title.
       Every star row in the widget belongs to a review, so reading one of
       those would show whatever the top review happened to score — the score
       is the only bare number outside the cards. */
    var headRating = 0;
    Array.prototype.some.call(w.querySelectorAll('*'), function (node) {
      if (node.children.length || node.closest('.jdgm-review-card')) return false;
      var t = node.textContent.trim();
      if (!/^\d(\.\d)?$/.test(t)) return false;
      headRating = parseFloat(t);
      return true;
    });

    var tabs = Array.prototype.slice.call(w.querySelectorAll('.jdgm-tab')).map(function (t) {
      var text = t.textContent.replace(/\s+/g, ' ').trim();
      var m = text.match(/^(.*?)\s*\((\d[\d,]*)\)\s*$/);
      return {
        node: t,
        label: m ? m[1] : text,
        count: m ? m[2] : '',
        active: t.classList.contains('jdgm-tab--active')
      };
    });

    var countFromTab = '';
    tabs.forEach(function (t) { if (t.active && t.count) countFromTab = t.count; });

    /* These attributes are what the reviews page builds its bars from, and
       they are read here in the same way — but the product widget does not
       currently publish them, so the bars stay hidden on a PDP.

       It is not a setting: `jdgmSettings.widget_show_histogram` is already
       true. The two pages run different generations of Judge.me's widget.
       /pages/our-reviews uses the classic all-reviews widget, which emits
       `.jdgm-histogram__row` with the per-rating counts; the product widget
       here is their Vue "revamp" build, which renders no histogram at all.

       So this stays as a reader rather than a guess: if the product widget
       ever publishes a breakdown, the bars and the recommend share light up
       on their own, and until then neither is estimated from the handful of
       reviews on screen. */
    var histRows = Array.prototype.slice.call(w.querySelectorAll('.jdgm-histogram__row[data-rating]'));
    var hist = histRows.map(function (r) {
      return {
        rating: parseInt(r.getAttribute('data-rating'), 10),
        freq: parseInt(r.getAttribute('data-frequency'), 10) || 0,
        pct: parseFloat(r.getAttribute('data-percentage')) || 0
      };
    }).sort(function (a, b) { return b.rating - a.rating; });

    return {
      reviews: reviews,
      rating: headRating,
      count: countFromTab,
      tabs: tabs,
      hist: hist,
      write: w.querySelector('.jm-button--primary, .jdgm-write-rev-link'),
      ask: w.querySelector('.jm-action-buttons__button.jm-button--secondary, .jdgm-ask-question-link')
    };
  }

  /* ── Painting ─────────────────────────────────────────────── */

  var STAR_SVG =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.6 1.1 6.45L12 17.45 6.2 20.5l1.1-6.45-4.7-4.6 6.5-.95z"/></svg>';

  /* A star is drawn twice: a dim one for the shape and a gold one over it,
     clipped to the fraction earned, so a 4.5 reads as 4.5 rather than being
     rounded to a rating nobody gave. */
  function starsInto(node, rating) {
    node.textContent = '';
    for (var i = 1; i <= 5; i++) {
      var fill = Math.max(0, Math.min(1, rating - i + 1));
      var star = document.createElement('span');
      star.className = 'mt-pr-star';
      star.innerHTML =
        '<span class="mt-pr-star-off">' + STAR_SVG + '</span>' +
        '<span class="mt-pr-star-on" style="width:' + (fill * 100) + '%">' + STAR_SVG + '</span>';
      node.appendChild(star);
    }
    node.setAttribute('aria-label', rating.toFixed(1) + ' out of 5 stars');
  }

  function paintSummary(data) {
    if (!data.rating) return;
    el.summary.hidden = false;
    el.score.textContent = data.rating.toFixed(1);
    starsInto(el.stars, data.rating);
    el.count.textContent = data.count || String(data.reviews.length);

    /* With no histogram there is nothing for the second column, so the score
       should not sit beside a large empty panel. */
    el.summary.classList.toggle('mt-pr-summary--solo', !data.hist.length);
    if (!data.hist.length) return;

    el.bars.hidden = false;
    el.bars.textContent = '';
    var total = 0;
    var recommended = 0;
    data.hist.forEach(function (h) {
      total += h.freq;
      if (h.rating >= 4) recommended += h.freq;
    });

    data.hist.forEach(function (h) {
      var row = document.createElement('div');
      row.className = 'mt-pr-bar';
      row.innerHTML =
        '<span class="mt-pr-bar-label">' + h.rating + ' <span>★</span></span>' +
        '<span class="mt-pr-bar-track"><span class="mt-pr-bar-fill"></span></span>' +
        '<span class="mt-pr-bar-pct"></span>';
      row.querySelector('.mt-pr-bar-pct').textContent = Math.round(h.pct) + '%';
      el.bars.appendChild(row);
      /* Next frame, so the width animates from zero rather than appearing
         already full. */
      requestAnimationFrame(function () {
        row.querySelector('.mt-pr-bar-fill').style.width = h.pct + '%';
      });
    });

    if (el.recommend && total) {
      /* Not a Judge.me metric — the share of 4 and 5 star reviews, which is
         how the reviews page states it too. */
      el.recommend.hidden = false;
      el.recPct.textContent = Math.round((recommended / total) * 100) + '%';
    }
  }

  function paintToolbar(data) {
    if (!data.tabs.length && !data.write) return;
    el.toolbar.hidden = false;

    if (el.tabs.children.length !== data.tabs.length) el.tabs.textContent = '';

    data.tabs.forEach(function (t, i) {
      var btn = el.tabs.children[i];
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mt-pr-tab';
        btn.setAttribute('role', 'tab');
        btn.innerHTML = '<span></span> <span class="mt-pr-tab-count"></span>';
        /* Proxy straight to their tab and let their widget do the work. */
        btn.addEventListener('click', function () {
          t.node.click();
          setTimeout(sync, 400);
        });
        el.tabs.appendChild(btn);
      }
      btn.children[0].textContent = t.label;
      btn.children[1].textContent = t.count ? '(' + t.count + ')' : '';
      btn.classList.toggle('is-on', t.active);
      btn.setAttribute('aria-selected', t.active ? 'true' : 'false');
    });

    if (!el.actions.children.length) {
      [
        { src: data.write, cls: 'mt-pr-btn mt-pr-btn--primary', label: LABEL.write },
        { src: data.ask, cls: 'mt-pr-btn mt-pr-btn--ghost', label: LABEL.ask }
      ].forEach(function (spec) {
        if (!spec.src) return;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = spec.cls;
        b.textContent = spec.label;
        b.addEventListener('click', function () { spec.src.click(); });
        el.actions.appendChild(b);
      });
    }
  }

  function cardFor(r) {
    var card = document.createElement('article');
    card.className = 'mt-pr-card';

    var top = document.createElement('div');
    top.className = 'mt-pr-card-top';
    var stars = document.createElement('div');
    stars.className = 'mt-pr-card-stars';
    starsInto(stars, r.rating);
    top.appendChild(stars);

    if (r.verified) {
      var v = document.createElement('span');
      v.className = 'mt-pr-verified';
      v.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="overflow:visible"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3"/></svg>' +
        '<span></span>';
      v.querySelector('span').textContent = LABEL.verified;
      top.appendChild(v);
    }
    card.appendChild(top);

    var body = document.createElement('p');
    body.className = 'mt-pr-card-body';
    if (r.body) {
      body.textContent = r.body;
    } else {
      body.classList.add('mt-pr-card-body--none');
      body.textContent = 'Rated ' + r.rating + ' out of 5. No written review.';
    }
    card.appendChild(body);

    var foot = document.createElement('div');
    foot.className = 'mt-pr-card-foot';
    var av = document.createElement('span');
    av.className = 'mt-pr-avatar';
    av.textContent = r.initial;
    av.setAttribute('aria-hidden', 'true');
    foot.appendChild(av);

    var who = document.createElement('div');
    var nm = document.createElement('p');
    nm.className = 'mt-pr-name';
    nm.textContent = r.name || 'Anonymous';
    who.appendChild(nm);
    /* Judge.me has no "role" field. Verified buyer is the one thing it does
       assert about a reviewer, so that is the only byline shown. */
    if (r.verified) {
      var role = document.createElement('p');
      role.className = 'mt-pr-role';
      role.textContent = LABEL.buyer;
      who.appendChild(role);
    }
    foot.appendChild(who);
    card.appendChild(foot);
    return card;
  }

  /* How many rows the wall is dealt into at the current width.

     Two rows exist so opposing drift reads as motion rather than as one long
     conveyor. A phone has room for one column, so section-mt-pdp-reviews.css
     turns the wall into a single upward scroller at 749 — matching the Our
     Reviews page, which drops to one column at 680 (columnsFor in
     sections/mt-reviews-toolbar.liquid).

     The split has to follow that, which is why this exists rather than the
     CSS simply hiding the second row. The rows do not repeat each other, so
     a hidden row takes the reviews it holds with it — half of them, visible
     nowhere else on a phone. Dealing into one group instead puts every
     review in the column that is actually on screen.

     749 must stay in step with the media query in the stylesheet: if they
     drift, a row is either emptied while still laid out sideways, or shown
     drifting the wrong way. */
  var VERTICAL_MQ = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

  function rowCount() {
    return VERTICAL_MQ && VERTICAL_MQ.matches ? 1 : el.tracks.length;
  }

  /* Split across the rows in use so none repeats another, then each row
     holds its own set twice so the drift can loop without a seam. */
  function paintRows(reviews) {
    if (!reviews.length) {
      el.rows.hidden = true;
      el.empty.hidden = false;
      return;
    }
    el.empty.hidden = true;
    el.rows.hidden = false;

    /* Too few to drift: a loop of one or two cards reads as a stutter, so
       they are laid out still, side by side, in the first row only. This
       used to hide the rows AND the empty line, which — with Judge.me's own
       widget hidden too — left a product with 1–3 reviews showing none. */
    var still = reviews.length < MIN_FOR_ROWS;
    el.rows.classList.toggle('mt-pr-rows--still', still);
    if (still) {
      for (var s = 0; s < el.tracks.length; s++) {
        var t = el.tracks[s];
        t.textContent = '';
        if (t.parentNode) t.parentNode.hidden = s > 0;
      }
      reviews.forEach(function (r) { el.tracks[0].appendChild(cardFor(r)); });
      return;
    }

    var rows = rowCount();
    var per = Math.ceil(reviews.length / rows);

    for (var i = 0; i < el.tracks.length; i++) {
      var track = el.tracks[i];
      var row = track.parentNode;

      /* Rows past the count for this width are emptied as well as hidden.
         `hidden` keeps them out of the accessibility tree, and clearing the
         cards means a later re-deal never has stale copies to duplicate. */
      if (i >= rows) {
        track.textContent = '';
        if (row) row.hidden = true;
        continue;
      }
      if (row) row.hidden = false;

      var set = reviews.slice(i * per, (i + 1) * per);
      if (!set.length) set = reviews;
      track.textContent = '';
      set.forEach(function (r) { track.appendChild(cardFor(r)); });
      if (!reduceMotion) {
        set.forEach(function (r) {
          var clone = cardFor(r);
          clone.setAttribute('aria-hidden', 'true');
          track.appendChild(clone);
        });
      }
    }
  }

  /* ── Keeping up with the widget ───────────────────────────── */
  var lastSig = '';
  var syncing = false;

  function sync() {
    if (syncing) return;
    var data = harvest();
    if (!data) return;

    /* Their widget renders its shell before the reviews land, so an empty
       harvest means "not yet", not "no reviews". */
    if (!data.reviews.length && !data.rating) return;

    /* The verified count is in the signature on purpose. Judge.me fills a
       card's body first and adds its verified badge a beat later, so a
       signature of only ratings and counts settles while every card still
       looks unverified — and then nothing ever repaints them. */
    var verified = 0;
    data.reviews.forEach(function (r) { if (r.verified) verified++; });

    var sig = data.rating + '|' + data.count + '|' + data.reviews.length + '|' + verified + '|' +
      data.tabs.map(function (t) { return t.active ? '1' : '0'; }).join('') + '|' + data.hist.length;
    if (sig === lastSig) return;
    lastSig = sig;

    syncing = true;
    try {
      paintSummary(data);
      paintToolbar(data);
      paintRows(data.reviews);
      root.classList.add('is-ready');
      document.documentElement.classList.add('mt-pr-hide-jdgm');
    } finally {
      syncing = false;
    }
  }

  sync();

  /* A product with no reviews leaves the widget with nothing to read, so
     `sync` keeps returning early and the panel would stay empty for good.
     Give it a fair chance to load, then say which of the two silences this
     is:

       widget present, nothing in it  -> a product with no reviews yet, so
                                         show the empty line
       no widget at all               -> this template carries no Judge.me
                                         block, so close the accordion row
                                         rather than leave one that opens
                                         onto nothing. */
  setTimeout(function () {
    if (root.classList.contains('is-ready')) return;
    if (widget()) {
      el.empty.hidden = false;
      root.classList.add('is-ready');
      return;
    }
    var item = root.closest('.product-details-accordion__item');
    if (item) item.style.display = 'none';
    else root.style.display = 'none';
  }, 10000);

  /* The widget is a Vue app: it re-renders on tab changes, on "Show more",
     and once more when its reviews finish loading. Watching the document is
     simpler than guessing which of those happened, and the signature above
     keeps the repaint to the times something actually changed.

     Our own painting is confined to `root`, which this ignores, so it cannot
     react to its own output. */
  /* Crossing the one-column breakpoint — a rotation, or a desktop window
     dragged narrow — has to re-deal, or the wall keeps the split it was
     given for the other layout: two rows' worth of cards stacked into one
     vertical column, or a single column's worth laid out sideways with the
     second row empty.

     `lastSig` is cleared because sync() returns early when the signature is
     unchanged, and nothing about the reviews themselves has changed here —
     only the shape they belong in.

     addListener is the pre-Safari-14 spelling of addEventListener on a media
     query list; both are kept because the rest of this file is ES5 and runs
     without a build step. */
  if (VERTICAL_MQ) {
    var onLayoutChange = function () { lastSig = ''; sync(); };
    if (VERTICAL_MQ.addEventListener) VERTICAL_MQ.addEventListener('change', onLayoutChange);
    else if (VERTICAL_MQ.addListener) VERTICAL_MQ.addListener(onLayoutChange);
  }

  var pending = null;
  var obs = new MutationObserver(function (records) {
    var relevant = records.some(function (r) { return !root.contains(r.target); });
    if (!relevant || pending) return;
    pending = setTimeout(function () { pending = null; sync(); }, 200);
  });
  obs.observe(document.body, { childList: true, subtree: true });
})();
